import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { HandleGridBody } from "components/common/HandleGridBody.js";
import AddClients from "./AddViewClients.jsx";
import { useNavigate } from "react-router-dom";
import SearchTextBox from "../../components/SearchComponent.jsx";
import { FilterComponent } from "components/common/FiltersComponent";
import {
  changeClientEditPermission,
  getClientsList,
} from "../../actions/clients.action.js";
import { RedirectCell, GRID_PAGE_SIZES } from "components/common/GridComponents.jsx";
import { arrowRotateCwIcon as refreshIcon } from "@progress/kendo-svg-icons";
import { SvgIcon } from "@progress/kendo-react-common";
import MattersGridComponent from "../../components/Matters/MattersGridComponent.jsx";
import styles from "./Clients.module.scss";
import {
  updateCurrentUserSettings,
  useCurrentUserSettings,
} from "services/userSettingsService.js";
import { getAccessRightsData, useCurrentUser } from "services/currentUserService.js";
import MatterForm from "pages/Matters/MatterForm";
import addMatter from "../../assets/add-matter.svg";
import { useCompany } from "services/companyService.js";
import ActionbarSkelton from "components/common/Loaders/ActionbarSkelton.jsx";

const USER_SETTING_PAGE_SIZE_KEY = "ClientListPageSize";

const DetailComponent = (props) => {
  const data = props.dataItem.matters;
  const matters = data ?? [];
  return matters.length > 0 ? (
    <MattersGridComponent Clientdata={matters} fromClient={true} />
  ) : (
    <div className="text-center fw-semibold">No matters found</div>
  );
};

const Clients = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: userSettings } = useCurrentUserSettings();
  const { data: currentUser } = useCurrentUser();
  const { data: company } = useCompany();

  const [dataState, setDataState] = useState({
    take:
      Number(
        userSettings?.find((s) => s.key === USER_SETTING_PAGE_SIZE_KEY)?.value,
      ) || 50,
    skip: 0,
    sort: [{ field: "clientRef", dir: "asc" }],
    pageable: {
      pageSizes: GRID_PAGE_SIZES,
    },
  });
  const [list, setLists] = useState({ data: [], total: 50 });
  const [eventDataState, setEventDataState] = useState();
  const [filterParameters, setFilterParameters] = useState(() => ({
    branch: -2,
    appCode: undefined,
    feeEarnerRef: currentUser?.userRef,
    teamId: -2,
  }));
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientMode, setClientMode] = useState("view"); // "view", "edit", or "add"
  const [isClientSaving, setIsClientSaving] = useState(false);
  const [isRefreshed, setIsRefreshed] = useState(false);
  const [searchValue, setSearchValue] = useState();
  const [isSearched, setIsSearched] = useState(false);
  const [loadedContent, setLoadedContent] = useState(true);
  const [allowAccessData, setAllowAccessData] = useState(null);
  const [reference, setReference] = useState("");
  const [clientId, setClientId] = useState("");
  const [showMatterForm, setShowMatterForm] = useState(false);
  const [matterFormClient, setMatterFormClient] = useState(null);

  const viewEdit = useSelector((state) => state.clients.clinetEditPermission);
  const isLoadingClientsList =
    useSelector((state) => state.clients.isLoading);
  const searchRef = useRef();

  useEffect(() => {
    const saved = Number(
      userSettings?.find((s) => s.key === USER_SETTING_PAGE_SIZE_KEY)?.value,
    );
    if (saved && saved !== dataState.take) {
      setDataState((prev) => ({ ...prev, take: saved, skip: 0 }));
    }
  }, [userSettings]);

  const dataStateChange = async (e) => {
    const newPageSize = e.dataState.take;
    const currentPageSize = dataState.take;

    const updatedDataState = {
      ...e.dataState,
      skip: newPageSize !== currentPageSize ? 0 : e.dataState.skip,
      pageable: dataState.pageable, // Forcefully re-apply pageable
    };

    if (newPageSize !== currentPageSize) {
      try {
        await updateCurrentUserSettings([
          { key: USER_SETTING_PAGE_SIZE_KEY, value: newPageSize },
        ]);
      } catch (err) {
        console.error("Failed to update page size", err);
      }
    }

    setDataState(updatedDataState);
    setEventDataState({ event: updatedDataState });
  };
  const handleRefresh = () => {
    setIsSearched(false);
    setIsRefreshed(!isRefreshed);
    const resetState = eventDataState
      ? { ...eventDataState.event, skip: 0 }
      : { ...dataState, skip: 0 };
    setDataState(resetState);
  };

  const getAccessRights = async () => {
    try {
      const data = await getAccessRightsData();
      setAllowAccessData(data?.data?.data);
      setLoadedContent(false);
    } catch {}
  };

  useEffect(() => {
    getAccessRights();
  }, []);

  // Sync Redux viewEdit state with local clientMode
  useEffect(() => {
    if (viewEdit) {
      setClientMode("edit");
    }
  }, [viewEdit]);

  const dataReceived = (list) => {
    if (list?.data) {
      setLists(list);
    }
  };

  const analyzeFilterParamters = (params) => {
    const newParams = {
      branch: params?.branch,
      appCode: params?.appCode,
      feeEarnerRef: params?.feeEarnerRef,
      teamId: params?.teamId,
    };
    if (JSON.stringify(newParams) !== JSON.stringify(filterParameters)) {
      setFilterParameters(newParams);
    }
  };

  const onClientRefClickActions = (props) => {
    setReference(props.dataItem?.clientRef);
    setClientId(props.dataItem?.clientId);
    setClientMode("view");
    setShowClientModal(true);
  };

  const ClientCell = (props) => (
    <td>
      <u
        style={{ cursor: "pointer" }}
        onClick={() => onClientRefClickActions(props)}>
        {props.dataItem.clientRef}
      </u>
    </td>
  );

  const NoOfMattersCell = (props) => (
    <td className="text-center">
      {props.dataItem.noOfMatters > 0 && props.dataItem.noOfMatters}
    </td>
  );

  const Redirect = (props) => (
    <RedirectCell handleClick={() => onClientRefClickActions(props)} />
  );

  const getHeaderItems = () =>
    loadedContent ? (
      <ActionbarSkelton />
    ) : (
      <div className="d-flex align-items-end flex-wrap gap-2 mb-3 justify-content-start">
        <FilterComponent
          allowAccessData={allowAccessData}
          updateFilters={analyzeFilterParamters}
          isRefreshed={isRefreshed}
          inlineLayout
        />

        <div
          className="flex-grow-1"
          style={{ minWidth: "200px", maxWidth: "280px" }}>
          <SearchTextBox
            ref={searchRef}
            width="100%"
            minWidth="200px"
            height="40px"
            changeFilterValue={(e) => {
              setIsSearched(true);
              setSearchValue(e);
            }}
          />
        </div>

        <Button
          onClick={handleRefresh}
          className={`${styles["refresh-icon"]} common-btn me-auto`}
          title="Refresh"
          style={{ minWidth: "20px", height: "40px" }}>
          <span className="d-flex align-items-center">
            <SvgIcon icon={refreshIcon} />
            <span className={`ms-1 ${styles["refresh-label-icon-only"]}`}>
              Refresh
            </span>
          </span>
        </Button>

        <Button
          className="common-btn"
          onClick={() => {
            setClientId(null);
            setClientMode("add");
            setShowClientModal(true);
          }}>
          Add Client
        </Button>
        {company?.modules?.ClientOnboarding && (
          <Button
            className="common-btn text-wrap"
            onClick={() => navigate(`/clients/onboarding`)}>
            Client Onboarding
          </Button>
        )}
      </div>
    );

  const AddMatterCell = (props) => {
    return (
      <td className="text-center cursor-pointer">
        <img
          src={addMatter}
          alt="Add Matter"
          title="Add Matter"
          width={18}
          height={18}
          onClick={() => {
            setMatterFormClient(props.dataItem);
            setShowMatterForm(true);
          }}
        />
      </td>
    );
  };

  return (
    <div className="page h-100 w-100 d-flex flex-column">
      {getHeaderItems()}
      <Grid
        {...dataState}
        data={list}
        detail={DetailComponent}
        expandField="expanded"
        onExpandChange={(e) => {
          e.dataItem.expanded = e.value;
          setLists({ data: [...list.data], total: list.total });
        }}
        onDataStateChange={dataStateChange}
        sortable
        resizable
        className={`fs-md flex-grow-1 overflow-auto ${styles["clientname-min-width"]}`}>
        <GridNoRecords>
          {isLoadingClientsList ? (
            <span></span>
          ) : (
            list.data && list.data.length === 0 && "No records available"
          )}
        </GridNoRecords>
        <GridColumn
          field="clientRef"
          title="Client Ref"
          cell={ClientCell}
          width="120px"
        />
        <GridColumn
          field="clientName"
          title="Client Name"
          className="gridBoldField text-wrap"
        />
        <GridColumn field="feeEarnerRef" title="Fee Earner" width="103px" />
        <GridColumn field="type" title="Type" width="120px" />
        <GridColumn field="phoneNo" title="Phone" width="150px" />
        <GridColumn field="email" title="Email" width="220px" />
        <GridColumn
          field="address"
          title="Address"
          className="text-wrap"
          width="220px"
        />
        <GridColumn
          field="noOfMatters"
          title="Matters"
          width="100px"
          cell={NoOfMattersCell}
        />
        <GridColumn width="45px" cell={AddMatterCell} />

        <GridColumn width="60px" cell={Redirect} />
      </Grid>
      {userSettings && (
        <HandleGridBody
          dataState={dataState}
          onDataReceived={dataReceived}
          searchValue={isSearched ? searchValue : null}
          isSearched={isSearched}
          currentComponent="clients"
          filterParameters={filterParameters}
          showLoader={isLoadingClientsList}
        />
      )}

      {showClientModal && (
        <Dialog
          className="dialog-xxl add-dialog"
          title={
            clientMode === "add"
              ? "Add Client"
              : clientMode === "edit"
                ? "Update Client"
                : "View Client"
          }
          onClose={() => {
            setShowClientModal(false);
            if (viewEdit) {
              dispatch(changeClientEditPermission());
            }
          }}>
          <AddClients
            clientId={clientId}
            formId={clientMode}
            title={
              clientMode === "add"
                ? "Add Client"
                : clientMode === "edit"
                  ? "Update Client"
                  : "View Client"
            }
            type={clientMode === "view" ? "VIEW" : undefined}
            reference={reference}
            onSavingStateChange={setIsClientSaving}
            onSaveSuccess={() => {
              if (clientMode === "edit") {
                setClientMode("view");
                dispatch(changeClientEditPermission());
              }
              dispatch(
                getClientsList(dataState.skip / dataState.take + 1, dataState.take),
              );
            }}
            onClose={() => {
              setShowClientModal(false);
              if (viewEdit) {
                dispatch(changeClientEditPermission());
              }
              dispatch(
                getClientsList(dataState.skip / dataState.take + 1, dataState.take),
              );
            }}
          />
          <DialogActionsBar>
            <div className="dialog-footer">
              {clientMode === "view" ? (
                <Button
                  type="button"
                  onClick={() => {
                    setClientMode("edit");
                    dispatch(changeClientEditPermission());
                  }}
                  className="common-btn border-0 me-5">
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    className="common-btn bgBtnSecondary border-0 me-2"
                    onClick={() => {
                      if (clientMode === "edit") {
                        setClientMode("view");
                        dispatch(changeClientEditPermission());
                      } else {
                        setShowClientModal(false);
                      }
                    }}>
                    Cancel
                  </Button>
                  <Button
                    className="common-btn me-4"
                    type="submit"
                    disabled={isClientSaving}
                    onClick={() =>
                      document.getElementById("client-submitter").click()
                    }>
                    {isClientSaving
                      ? "Saving..."
                      : clientMode === "add"
                        ? "Add Client"
                        : "Update Client"}
                  </Button>
                </>
              )}
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
      {showMatterForm && (
        <MatterForm
          setshowMatterForm={() => {
            setShowMatterForm(false);
            setMatterFormClient(null);
          }}
          clientData={matterFormClient}
        />
      )}
    </div>
  );
};

export default Clients;
